import { Button, ButtonProps } from "@hortis/ui/button";
import { PhotoPlaceholder } from "@hortis/ui/photo-placeholder";
import { TwicImg } from "@twicpics/components/react";
import type { MaterialImageFieldsFragment } from "generated/graphql";
import { type HTMLAttributes } from "react";
import { Lightbox, LightboxTrigger } from "src/components/lightbox";
import type { useThumbnailPhoto } from "src/utils/photos";
import { twMerge } from "tailwind-merge";

interface RecordPageHeaderProps {
  children: React.ReactNode;
}

const RecordPageHeader = ({
  children,
  className,
  ...props
}: RecordPageHeaderProps & HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      className={twMerge(
        "record-page-header flex gap-3 items-center",
        className,
      )}
      data-cy="record-header"
      {...props}
    >
      {children}
    </div>
  );
};

interface RecordPageHeaderThumbnailProps {
  thumbnail: ReturnType<typeof useThumbnailPhoto> | null | undefined;
  additionalPhotos:
    | ReadonlyArray<MaterialImageFieldsFragment>
    | undefined
    | null;
  photoTitle?: string;
  grayscale: boolean;
  handleUploadImage?: () => void;
  className?: string;
}

const RecordPageHeaderThumbnail = ({
  thumbnail,
  additionalPhotos,
  photoTitle = "",
  grayscale,
  handleUploadImage,
  className,
}: RecordPageHeaderThumbnailProps) => {
  return (
    <div
      className={twMerge(
        "record-page-header-thumbnail h-[188px] w-full",
        className,
      )}
    >
      {thumbnail == null ? (
        <PhotoPlaceholder
          size="md"
          className="h-full w-full rounded-lg"
          handleUploadImage={handleUploadImage}
        />
      ) : (
        <Lightbox
          photo={thumbnail}
          photos={additionalPhotos}
          title={photoTitle}
        >
          <LightboxTrigger className="block size-full overflow-hidden rounded-lg outline-hidden ring-primary-200/70 focus-visible:ring-3">
            <TwicImg
              src={`${thumbnail.bucketName}/${thumbnail.fileName}`}
              eager
              focus="auto"
              mode="cover"
              className={twMerge(
                "h-[188px] overflow-hidden rounded-xl transition-transform hover:scale-[102%]",
                grayscale && "grayscale",
              )}
            />
          </LightboxTrigger>
        </Lightbox>
      )}
    </div>
  );
};

const RecordPageHeaderTrashRecordAlert = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return <div>{children}</div>;
};

const RecordPageHeaderContent = ({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className={twMerge("flex w-full flex-col", className)} {...props}>
      {children}
    </div>
  );
};

const RecordPageHeaderPropertiesSummary = ({
  children,
}: {
  children: React.ReactNode;
}) => (
  <div
    data-cy="record-header-summary"
    className="flex flex-row flex-wrap items-center gap-2"
  >
    {children}
  </div>
);

const RecordPageHeaderPropertiesItem = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    data-cy="record-header-properties-item"
    className={twMerge(
      "flex items-center gap-1 text-sm font-medium text-grey-500",
      className,
    )}
    {...props}
  />
);

const RecordPageHeaderTitleGroup = ({
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className="mb-1 flex flex-col gap-0.5" {...props}>
      {children}
    </div>
  );
};
const RecordPageHeaderTitle = ({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      data-cy="record-header-title"
      className={twMerge(
        "title-text text-lg font-semibold text-grey-900 flex items-center gap-2",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
};

const RecordPageHeaderSubtitle = ({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => {
  return (
    <p
      data-cy="record-header-subtitle"
      className={twMerge(
        "subtitle-text text-base font-medium text-grey-500",
        className,
      )}
      {...props}
    >
      {children}
    </p>
  );
};

const RecordPageHeaderReference = ({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className={twMerge("flex gap-5 items-center", className)} {...props}>
      {children}
    </div>
  );
};

const RecordPageHeaderReferenceLabel = ({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      className={twMerge(
        "hidden lg:block text-grey-500 font-medium text-sm",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
};

const RecordPageHeaderReferenceButton = ({
  children,
  className,
  ...props
}: ButtonProps) => {
  return (
    <Button
      variant="tertiaryGray"
      size="sm"
      className={twMerge(
        "-mx-2 py-1 px-2 hover:text-grey-900 transition flex items-center gap-2 truncate rounded-lg text-sm font-medium text-grey-900",
        className,
      )}
      {...props}
    >
      {children}
    </Button>
  );
};

const RecordPageHeaderButtons = ({
  children,
  className,
}: { children: React.ReactNode } & HTMLAttributes<HTMLDivElement>) => {
  return <div className={twMerge("flex gap-1", className)}>{children}</div>;
};

export {
  RecordPageHeader,
  RecordPageHeaderButtons,
  RecordPageHeaderContent,
  RecordPageHeaderPropertiesSummary,
  RecordPageHeaderPropertiesItem,
  RecordPageHeaderThumbnail,
  RecordPageHeaderTitleGroup,
  RecordPageHeaderTitle,
  RecordPageHeaderSubtitle,
  RecordPageHeaderTrashRecordAlert,
  RecordPageHeaderReference,
  RecordPageHeaderReferenceLabel,
  RecordPageHeaderReferenceButton,
};
